import React, { useState } from 'react'
import styles from './ActionPanel.module.css'

const API_URL = process.env.REACT_APP_API_URL || ''

function getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
}

const ACTION_CONFIG = {
    TAILOR_CV:   { icon: '📝', label: 'Tailor CV',   color: '#7c3aed', bg: '#7c3aed18' },
    TAKE_COURSE: { icon: '🎓', label: 'Take Course', color: '#0ea5e9', bg: '#0ea5e918' },
    READ_NEWS:   { icon: '🗞', label: 'Read News',   color: '#10b981', bg: '#10b98118' },
    APPLY:       { icon: '💼', label: 'Apply',       color: '#f59e0b', bg: '#f59e0b18' },
}

const RELATED_ICON = { job: '💼', course: '📚', news: '📰', event: '📅' }

const getConfig = (actionType) =>
    ACTION_CONFIG[actionType] || { icon: '⚡', label: actionType || 'Action', color: '#432C5B', bg: '#432C5B18' }

/**
 * ActionPanel renders one row per action GROUP (action_type).
 * Each group can have multiple related content items (courses/news/events).
 * Prop: actions = [{action_type, description, status, primary_content_id, relatedItems:[]}]
 */
const ActionPanel = ({ actions }) => {
    // saveState: { "primaryContentId:action_type": 'idle' | 'saving' | 'saved' | 'error' }
    // AND individual item saves: { "action_id": 'idle' | 'saving' | 'saved' }
    const [saveState, setSaveState] = useState(() => {
        const init = {}
        actions.forEach(a => {
            const k = `${a.primary_content_id}:${a.action_type}`
            init[k] = a.status === 'accepted' ? 'saved' : 'idle'
            if (a.relatedItems) {
                a.relatedItems.forEach(item => {
                    init[item.action_id] = item.status === 'accepted' ? 'saved' : 'idle'
                })
            }
        })
        return init
    })

    const handleAddGroup = async (action) => {
        const k = `${action.primary_content_id}:${action.action_type}`
        if (saveState[k] === 'saving' || saveState[k] === 'saved') return
        setSaveState(prev => ({ ...prev, [k]: 'saving' }))
        try {
            const token = getCookie('token')
            const res = await fetch(`${API_URL}/feed/save-action`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    primary_content_id: action.primary_content_id,
                    action_type:        action.action_type,
                }),
            })
            if (res.ok) {
                setSaveState(prev => {
                    const next = { ...prev, [k]: 'saved' }
                    // Mark all related items as saved too
                    if (action.relatedItems) {
                        action.relatedItems.forEach(item => { next[item.action_id] = 'saved' })
                    }
                    return next
                })
            } else {
                setSaveState(prev => ({ ...prev, [k]: 'error' }))
                setTimeout(() => setSaveState(prev => ({ ...prev, [k]: 'idle' })), 2500)
            }
        } catch {
            setSaveState(prev => ({ ...prev, [k]: 'error' }))
            setTimeout(() => setSaveState(prev => ({ ...prev, [k]: 'idle' })), 2500)
        }
    }

    const handleAddItem = async (e, item) => {
        e.preventDefault() // prevent navigating to URL
        e.stopPropagation()
        const k = item.action_id
        if (saveState[k] === 'saving' || saveState[k] === 'saved') return
        setSaveState(prev => ({ ...prev, [k]: 'saving' }))
        try {
            const token = getCookie('token')
            const res = await fetch(`${API_URL}/feed/save-action-by-id`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ action_id: item.action_id }),
            })
            if (res.ok) {
                setSaveState(prev => ({ ...prev, [k]: 'saved' }))
            } else {
                setSaveState(prev => ({ ...prev, [k]: 'error' }))
                setTimeout(() => setSaveState(prev => ({ ...prev, [k]: 'idle' })), 2500)
            }
        } catch {
            setSaveState(prev => ({ ...prev, [k]: 'error' }))
            setTimeout(() => setSaveState(prev => ({ ...prev, [k]: 'idle' })), 2500)
        }
    }

    if (!actions || actions.length === 0) return null

    return (
        <div className={styles.panel}>
            <h4 className={styles.panelHeading}>⚡ ACTIONS YOU CAN TAKE</h4>
            <div className={styles.groupList}>
                {actions.map((action, idx) => {
                    const config  = getConfig(action.action_type)
                    const k       = `${action.primary_content_id}:${action.action_type}`
                    const groupState = saveState[k] || 'idle'
                    const isGroupSaved  = groupState === 'saved'
                    const isGroupSaving = groupState === 'saving'
                    const isGroupError  = groupState === 'error'
                    
                    // Check if *all* related items are saved individually
                    const allItemsSaved = action.relatedItems && action.relatedItems.length > 0 && 
                        action.relatedItems.every(item => saveState[item.action_id] === 'saved')

                    const isEffectivelySaved = isGroupSaved || allItemsSaved

                    return (
                        <div key={`${action.action_type}-${idx}`} className={`${styles.group} ${isEffectivelySaved ? styles.groupSaved : ''}`}>
                            {/* Top row: type pill + description + button */}
                            <div className={styles.groupTop}>
                                <div className={styles.groupLeft}>
                                    <span
                                        className={styles.typePill}
                                        style={{ background: config.bg, color: config.color }}
                                    >
                                        {config.icon} {config.label}
                                    </span>
                                    <p className={styles.desc}>{action.description}</p>
                                </div>
                                <button
                                    className={`${styles.addBtn} ${isEffectivelySaved ? styles.addBtnSaved : ''} ${isGroupError ? styles.addBtnError : ''}`}
                                    onClick={() => handleAddGroup(action)}
                                    disabled={isEffectivelySaved || isGroupSaving}
                                    title={isEffectivelySaved ? 'Already in your actions' : (action.relatedItems?.length > 1 ? 'Add All' : 'Add to My Actions')}
                                >
                                    {isGroupSaving ? (
                                        <span className={styles.spinner} />
                                    ) : isEffectivelySaved ? (
                                        '✓ Added'
                                    ) : isGroupError ? (
                                        '! Retry'
                                    ) : (action.relatedItems?.length > 1 ? '+ Add All' : '+ Add Action')}
                                </button>
                            </div>

                            {/* Related content chips */}
                            {action.relatedItems && action.relatedItems.length > 0 && (
                                <div className={styles.relatedList}>
                                    {action.relatedItems.map((item, i) => {
                                        const itemState = saveState[item.action_id] || 'idle'
                                        const isItemSaved = itemState === 'saved' || isGroupSaved
                                        const isItemSaving = itemState === 'saving'
                                        
                                        return (
                                        <div key={i} className={styles.relatedChipWrapper}>
                                            <a
                                                href={item.url || '#'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={styles.relatedChip}
                                                onClick={e => !item.url && e.preventDefault()}
                                            >
                                                <span className={styles.relatedIcon}>{RELATED_ICON[item.type] || '🔗'}</span>
                                                <span className={styles.relatedTitle}>{item.title}</span>
                                                {item.subtitle && (
                                                    <span className={styles.relatedSub}> · {item.subtitle}</span>
                                                )}
                                                {item.url && <span className={styles.relatedArrow}>↗</span>}
                                            </a>
                                            <button
                                                className={`${styles.addItemBtn} ${isItemSaved ? styles.addItemBtnSaved : ''}`}
                                                onClick={(e) => handleAddItem(e, item)}
                                                disabled={isItemSaved || isItemSaving}
                                                title={isItemSaved ? 'Added' : 'Add this item'}
                                            >
                                                {isItemSaving ? (
                                                    <span className={styles.spinnerSmall} />
                                                ) : isItemSaved ? (
                                                    '✓'
                                                ) : (
                                                    '+'
                                                )}
                                            </button>
                                        </div>
                                    )})}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default ActionPanel
